import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShieldCheck, DeviceMobile } from '@phosphor-icons/react';

export default function Profile() {
  const { user } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [enabled, setEnabled] = useState(false);

  const setupMut = useMutation({
    mutationFn: () => api.post('/auth/mfa/setup'),
    onSuccess: (r) => setSetup(r.data), onError: (e) => toast.error(errMsg(e)),
  });
  const enableMut = useMutation({
    mutationFn: () => api.post('/auth/mfa/enable', { code }),
    onSuccess: () => { setEnabled(true); toast.success('MFA enabled. Next sign-in will require your authenticator code.'); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const mfaOn = enabled || user?.mfa_enabled;
  const fileRef = useRef(null);
  const [photoV, setPhotoV] = useState(0);
  const [hasPhoto, setHasPhoto] = useState(!!user?.photo_path);
  const [contact, setContact] = useState({ phone: user?.phone || '', alt_email: user?.alt_email || '', address: user?.address || '', emergency_contact: user?.emergency_contact || '' });
  const [vPhone, setVPhone] = useState('');
  const [vCode, setVCode] = useState('');
  const [vSent, setVSent] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const { data: ccData } = useQuery({ queryKey: ['phone-code'], queryFn: () => api.get('/sms/phone-code').then((r) => r.data) });
  const cc = ccData?.phone_code || '';
  const contactMut = useMutation({
    mutationFn: () => api.patch('/profile', contact),
    onSuccess: () => toast.success('Contact details saved'),
    onError: (e) => toast.error(errMsg(e)),
  });
  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/people/user/${user.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasPhoto(true); setPhotoV(Date.now());
      toast.success('Profile photo updated');
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-2xl" data-testid="profile-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">Security</h1>

      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6 stagger-in">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Account</p>
        <div className="flex items-center gap-4">
          <div className="relative">
            {hasPhoto
              ? <img src={`${API}/people/user/${user?.id}/photo?v=${photoV}`} alt="me" data-testid="profile-photo" className="w-16 h-16 rounded-full object-cover border border-slate-200" />
              : <div className="w-16 h-16 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-display font-bold text-xl" data-testid="profile-initials">{(user?.name || '?').split(' ').map((x) => x[0]).slice(0, 2).join('')}</div>}
          </div>
          <div className="flex-1">
            <p className="font-display text-xl font-bold">{user?.name}</p>
            <p className="text-sm text-slate-500">{user?.email} · {user?.role?.replace(/_/g, ' ')}</p>
            <button data-testid="profile-photo-upload" onClick={() => fileRef.current?.click()} className="text-xs underline text-slate-400 hover:text-slate-900 mt-1">{hasPhoto ? 'Change photo' : 'Upload photo'}</button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ''; }} />
          </div>
        </div>
        <div className="border-t border-slate-100 mt-5 pt-4 flex items-center justify-between flex-wrap gap-2" data-testid="zero-trust-self">
          <div>
            <p className="text-sm font-semibold">Sign out everywhere</p>
            <p className="text-xs text-slate-400">Instantly invalidates every active session on all devices (Zero Trust kill switch)</p>
          </div>
          <button data-testid="revoke-sessions-button" onClick={async () => {
            if (!window.confirm('Sign out of ALL devices, including this one?')) return;
            try { await api.post('/auth/revoke-sessions'); } catch (e) { console.warn('Revoke-sessions call failed (token may already be invalid):', e?.message); }
            localStorage.removeItem('or_token');
            window.location.href = '/login';
          }} className="text-xs border border-rose-300 text-rose-600 rounded px-3 py-2 hover:bg-rose-50 transition-colors">Sign out everywhere</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5" data-testid="profile-contact-form">
          <Input data-testid="profile-phone" placeholder="Mobile number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          <Input data-testid="profile-alt-email" placeholder="Alternate email" value={contact.alt_email} onChange={(e) => setContact({ ...contact, alt_email: e.target.value })} />
          <Input data-testid="profile-address" placeholder="Address" value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} />
          <Input data-testid="profile-emergency" placeholder="Emergency contact (name & phone)" value={contact.emergency_contact} onChange={(e) => setContact({ ...contact, emergency_contact: e.target.value })} />
        </div>
        <Button data-testid="profile-contact-save" size="sm" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={contactMut.isPending} onClick={() => contactMut.mutate()}>Save contact details</Button>
        <div className="border-t border-slate-100 mt-5 pt-4" data-testid="phone-verify-block">
          <p className="text-sm font-semibold flex items-center gap-1.5"><DeviceMobile size={16} /> Verify your mobile number {user?.phone_verified && <span className="text-green-700 text-xs">verified: {user.phone}</span>}</p>
          <p className="text-xs text-slate-400 mb-2">We send a one-time code by SMS. Your country code{cc ? ` (${cc})` : ''} is added automatically - just type your mobile number.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center border border-slate-200 rounded-md overflow-hidden bg-white">
              {cc && <span className="px-2.5 py-1.5 text-sm bg-slate-50 text-slate-600 border-e border-slate-200 font-medium" data-testid="phone-verify-cc">{cc}</span>}
              <Input data-testid="phone-verify-input" className="w-40 border-0 focus-visible:ring-0" placeholder="98765 43210" value={vPhone} onChange={(e) => setVPhone(e.target.value)} />
            </div>
            <Button data-testid="phone-verify-send" size="sm" variant="outline" disabled={!vPhone} onClick={async () => {
              try {
                const { data } = await api.post('/sms/verify/start', { phone: vPhone });
                setVSent(true); setDevCode(data.dev_code || null); toast.success(data.message);
              } catch (e) { toast.error(errMsg(e)); }
            }}>Send code</Button>
            {vSent && (<>
              <Input data-testid="phone-verify-code" className="w-28" placeholder="6-digit code" value={vCode} onChange={(e) => setVCode(e.target.value)} />
              <Button data-testid="phone-verify-check" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!vCode} onClick={async () => {
                try {
                  await api.post('/sms/verify/check', { phone: vPhone, code: vCode });
                  toast.success('Phone number verified'); setVSent(false); setVCode('');
                } catch (e) { toast.error(errMsg(e)); }
              }}>Verify</Button>
            </>)}
          </div>
          {devCode && <p className="text-xs text-amber-600 mt-1" data-testid="phone-verify-devcode">SMS is in stub mode - your code is <b>{devCode}</b></p>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 stagger-in" data-testid="mfa-panel">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={20} weight="duotone" className={mfaOn ? 'text-green-700' : 'text-slate-400'} />
          <h2 className="font-display text-xl font-semibold">Multi-factor authentication (TOTP)</h2>
          <span className={`ml-auto font-mono-label text-[10px] uppercase px-2 py-1 rounded ${mfaOn ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`} data-testid="mfa-status-badge">
            {mfaOn ? 'Enabled' : 'Not enabled'}
          </span>
        </div>
        <p className="text-sm text-slate-500 mb-4">Enabling MFA on an admin account clears the last go-live check.</p>

        {mfaOn ? (
          <p className="text-sm text-green-700" data-testid="mfa-enabled-message">✓ MFA is active. You'll be asked for a 6-digit code at every sign-in.</p>
        ) : !setup ? (
          <Button data-testid="mfa-setup-button" onClick={() => setupMut.mutate()} disabled={setupMut.isPending} className="bg-slate-900 hover:bg-slate-800">
            <DeviceMobile size={16} className="mr-1" /> Set up MFA
          </Button>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 items-start">
            <div className="border border-slate-200 rounded-md p-4 flex flex-col items-center gap-3">
              <QRCodeSVG value={setup.otpauth_uri} size={168} data-testid="mfa-qr-code" />
              <p className="text-xs text-slate-500 text-center">Scan with Google Authenticator, Authy, or any TOTP app</p>
              <p className="font-mono-label text-[10px] text-slate-400 break-all text-center">secret: {setup.secret}</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Enter the 6-digit code shown in your app:</p>
              <Input data-testid="mfa-enable-code-input" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="mb-3" />
              <Button data-testid="mfa-enable-button" disabled={code.length !== 6 || enableMut.isPending} onClick={() => enableMut.mutate()} className="w-full bg-green-700 hover:bg-green-600">Verify & enable</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
